import L from "leaflet";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { _miles } from "../../../../../utils/numberUtils";
import { BaseMapRender, PopupConfig } from "../../../../base/map/BaseMapRender";
import { ConfigTopActions } from "../../../../implementations/map/TopActions";

/** Mapa selector de lote/potrero: dibuja los lotes de la finca activa y, al hacer
 *  click en uno, lo entrega vía `onPick` (sin popup). Resalta el lote ya
 *  seleccionado. Ortomosaico sí, conteo no. */
export class LotePickerMapRender extends BaseMapRender<ClienteFincaLoteResponse> {
  /**
   * @param _onPick         Handler que recibe el lote elegido.
   * @param _selectedLoteId Id del lote ya seleccionado (se resalta), o undefined.
   * @param _filter         Restringe qué lotes se dibujan, o undefined (todos).
   */
  constructor(
    private readonly _onPick: (lote: ClienteFincaLoteResponse) => void,
    private readonly _selectedLoteId?: string,
    private readonly _filter?: (lote: ClienteFincaLoteResponse) => boolean,
  ) {
    super();
    // Al terminar de dibujar, trae al frente el lote ya seleccionado.
    this.setOnMapInitializeEnd(() => {
      if (!this._selectedLoteId) return;
      const group = this._groupList.find(
        (g) => g.lote.id === this._selectedLoteId,
      );
      group?.layer.bringToFront();
    });
  }

  /** No seleccionable → gris tenue (no clickeable). Seleccionable → estilo por
   *  defecto, resaltando en amarillo el lote ya elegido. */
  protected getStyle(
    lote: ClienteFincaLoteResponse,
    response: ClienteFincaLoteResponse | null,
  ): L.PathOptions {
    const style = super.getStyle(lote, response);
    if (!this.isLoteSelectable(lote)) {
      style.color = "#9ca3af";
      style.fillColor = "#9ca3af";
      style.fillOpacity = 0.3;
      style.opacity = 0.6;
      return style;
    }
    if (this._selectedLoteId && lote.id === this._selectedLoteId) {
      style.color = "yellow";
    }
    return style;
  }

  /** Solo son seleccionables los lotes que pasan el filtro (todos si no hay). */
  protected isLoteSelectable(lote: ClienteFincaLoteResponse): boolean {
    return this._filter ? this._filter(lote) : true;
  }

  /** Sin datos extra: basta el lote. */
  protected doInitData(): Promise<ClienteFincaLoteResponse[]> {
    return Promise.resolve([]);
  }

  /** El "response" del lote es el propio lote. */
  protected doGetResponse(
    lote: ClienteFincaLoteResponse,
    _responseList: ClienteFincaLoteResponse[],
  ): ClienteFincaLoteResponse | null {
    return lote;
  }

  protected doDispose(): void {}

  /** Tooltip fijo (según tipo de finca) + ortomosaico; sin conteo. */
  protected getConfigTopActions(): ConfigTopActions<ClienteFincaLoteResponse> {
    return {
      toolTipItems: [
        {
          key: "info",
          label: "Info",
          get: (_response, lote) => this._tooltip(lote),
        },
      ],
      withOrtomosaico: true,
      withConteo: false,
    };
  }

  /** Tooltip del lote: nombre + animales en ganadería; solo nombre en agricultura. */
  private _tooltip(lote: ClienteFincaLoteResponse): string {
    if (this._finca.isGanaderia && lote.ganaderiaCantidadAnimales > 0) {
      return `${lote.nombreLote}<br>${_miles(lote.ganaderiaCantidadAnimales)}`;
    }
    return lote.nombreLote;
  }

  /** El picker no usa popup: el click selecciona directo. */
  protected getPopupConfig(): PopupConfig<ClienteFincaLoteResponse> | null {
    return null;
  }

  /** Al hacer click en un lote: lo entrega vía `onPick`. */
  protected onResponseSelected(
    lote: ClienteFincaLoteResponse,
    _response: ClienteFincaLoteResponse | null,
    _bottomSheet: HTMLDivElement,
  ): void {
    this._onPick(lote);
  }
}
